/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/* eslint-disable @typescript-eslint/promise-function-async -- Pipeline передаёт adapter Promise напрямую в abort-race без дополнительной async-обёртки. */

import { SyncState } from "matrix-js-sdk/lib/sync";

import type {
  CallRecoveryDependencies,
  LocalMediaSnapshot,
} from "./CallRecoveryAdapters";
import type {
  CallRecoveryStage,
  FailureContextSeed,
  RetryableFailureHint,
} from "./CallRecoveryTypes";
import { raceWithAbort, throwIfAborted } from "./abort";

interface PipelineOperation {
  readonly stage: CallRecoveryStage;
  readonly subsystem: FailureContextSeed["subsystem"];
  readonly operation: string;
  readonly retryableHint?: RetryableFailureHint;
}

/** Метаданные операций централизованы и не размазываются по coordinator. */
const OPERATIONS = {
  waitForSync: {
    stage: "matrix_sync",
    subsystem: "matrix",
    operation: "wait_for_sync",
    retryableHint: "matrix_sync_failure",
  },
  assertRoomAccessible: {
    stage: "room_access",
    subsystem: "matrix",
    operation: "assert_room_accessible",
  },
  restartMembership: {
    stage: "own_membership",
    subsystem: "membership",
    operation: "restart_membership",
    retryableHint: "membership_manager_failure",
  },
  refreshFocus: {
    stage: "focus",
    subsystem: "focus",
    operation: "refresh_focus",
    retryableHint: "focus_call_not_found",
  },
  getCredentials: {
    stage: "credentials",
    subsystem: "sfu",
    operation: "get_fresh_credentials",
  },
  disconnectLiveKit: {
    stage: "livekit",
    subsystem: "livekit",
    operation: "disconnect_epoch",
  },
  connectLiveKit: {
    stage: "livekit",
    subsystem: "livekit",
    operation: "connect_fresh",
  },
  restoreMedia: {
    stage: "local_media",
    subsystem: "media",
    operation: "restore_required_tracks",
    retryableHint: "required_track_transport_failure",
  },
  verifyMatrix: {
    stage: "verification",
    subsystem: "matrix",
    operation: "verify_matrix_sync",
    retryableHint: "matrix_sync_failure",
  },
  verifyMembership: {
    stage: "verification",
    subsystem: "membership",
    operation: "verify_own_membership",
    retryableHint: "membership_manager_failure",
  },
  verifyFocus: {
    stage: "verification",
    subsystem: "focus",
    operation: "verify_focus",
    retryableHint: "focus_call_not_found",
  },
  verifyLiveKit: {
    stage: "verification",
    subsystem: "livekit",
    operation: "verify_livekit_connection",
    retryableHint: "temporary_service_unavailable",
  },
  verifyMedia: {
    stage: "verification",
    subsystem: "media",
    operation: "verify_required_tracks",
    retryableHint: "required_track_transport_failure",
  },
  verifySignals: {
    stage: "verification",
    subsystem: "recovery",
    operation: "verify_no_new_escalation_signal",
    retryableHint: "temporary_service_unavailable",
  },
  resumeUpstream: {
    stage: "verification",
    subsystem: "membership",
    operation: "resume_upstream",
    retryableHint: "membership_manager_failure",
  },
} as const satisfies Record<string, PipelineOperation>;

/** Ошибка конкретной границы pipeline с сохранённым исходным cause. */
export class CallRecoveryAttemptError extends Error {
  public constructor(
    public readonly context: FailureContextSeed,
    cause: unknown,
  ) {
    super(
      `Стадия recovery ${context.stage} завершилась ошибкой (${context.operation})`,
      { cause },
    );
    this.name = "CallRecoveryAttemptError";
  }
}

/** Нарушенный success invariant всегда является типизированной retryable ошибкой. */
export class CallRecoveryInvariantError extends Error {
  public readonly code = "RECOVERY_INVARIANT_FAILED";

  public constructor(public readonly invariant: string) {
    super(`Не выполнен recovery invariant: ${invariant}`);
    this.name = "CallRecoveryInvariantError";
  }
}

/** Аргументы ровно одной попытки без retry и browser globals. */
export interface RunCallRecoveryAttemptOptions {
  readonly dependencies: CallRecoveryDependencies;
  readonly mediaSnapshot: LocalMediaSnapshot;
  readonly signal: AbortSignal;
  readonly signalVersionAtStart: number;
  readonly getSignalVersion: () => number;
  readonly onStage: (stage: CallRecoveryStage) => void;
}

/**
 * Выполняет одну полную попытку hard recovery.
 *
 * Функция намеренно не владеет retry/backoff: любой следующий запуск снова
 * обязан пройти Matrix, membership, focus, credentials и остальные стадии.
 */
export async function runCallRecoveryAttempt(
  options: RunCallRecoveryAttemptOptions,
): Promise<void> {
  const {
    dependencies,
    mediaSnapshot,
    signal,
    signalVersionAtStart,
    getSignalVersion,
    onStage,
  } = options;
  const run = async <T>(
    metadata: PipelineOperation,
    operation: () => Promise<T> | T,
  ): Promise<T> => {
    throwIfAborted(signal);
    onStage(metadata.stage);
    try {
      return await raceWithAbort(Promise.resolve().then(operation), signal);
    } catch (error) {
      if (error instanceof CallRecoveryAttemptError) throw error;
      throw new CallRecoveryAttemptError(
        refineFailureContext(metadata, error),
        error,
      );
    }
  };

  await run(OPERATIONS.waitForSync, () =>
    dependencies.matrix.waitForSync(signal),
  );
  await run(OPERATIONS.assertRoomAccessible, () =>
    dependencies.matrix.assertRoomAccessible(signal),
  );
  await run(OPERATIONS.restartMembership, () =>
    dependencies.membership.restart(signal),
  );
  await run(OPERATIONS.refreshFocus, () => dependencies.focus.refresh(signal));

  // Hard recovery всегда создаёт новую transport epoch с новым config.
  const config = await run(OPERATIONS.getCredentials, () =>
    dependencies.credentials.getFresh(signal),
  );
  await run(OPERATIONS.disconnectLiveKit, () =>
    dependencies.livekit.disconnectEpoch(signal),
  );
  await run(OPERATIONS.connectLiveKit, () =>
    dependencies.livekit.connectFresh(config, signal),
  );
  await run(OPERATIONS.restoreMedia, () =>
    dependencies.media.restoreRequiredTracks(mediaSnapshot, signal),
  );

  onStage("verification");
  await verify(
    OPERATIONS.verifyMatrix,
    () => dependencies.matrix.getSyncState() === SyncState.Syncing,
    "matrix_syncing",
    run,
  );
  await verify(
    OPERATIONS.verifyMembership,
    () => dependencies.membership.isReady(),
    "own_membership_ready",
    run,
  );
  await verify(
    OPERATIONS.verifyFocus,
    () => dependencies.focus.isReady(),
    "focus_ready",
    run,
  );
  await verify(
    OPERATIONS.verifyLiveKit,
    () => dependencies.livekit.isConnected(),
    "livekit_connected",
    run,
  );
  await verify(
    OPERATIONS.verifyMedia,
    () => dependencies.media.verifyRequiredTracks(mediaSnapshot),
    "required_tracks_restored",
    run,
  );
  await verify(
    OPERATIONS.verifySignals,
    () => getSignalVersion() === signalVersionAtStart,
    "no_new_escalation_signal",
    run,
  );

  // Resume является частью success boundary. Его ошибка запрещает unfreeze.
  await run(OPERATIONS.resumeUpstream, () =>
    dependencies.membership.resumeUpstream(),
  );
  await verify(
    OPERATIONS.verifySignals,
    () => getSignalVersion() === signalVersionAtStart,
    "no_new_escalation_signal",
    run,
  );
}

function refineFailureContext(
  metadata: PipelineOperation,
  error: unknown,
): FailureContextSeed {
  if (metadata !== OPERATIONS.getCredentials || !isRecord(error)) {
    return metadata;
  }
  const operation = error.operation;
  if (operation === "openid") {
    return {
      ...metadata,
      subsystem: "openid",
      operation: "get_openid_token",
    };
  }
  if (operation === "jwt") {
    return {
      ...metadata,
      subsystem: "sfu",
      operation: "get_sfu_jwt",
    };
  }
  return metadata;
}

function isRecord(value: unknown): value is Record<PropertyKey, unknown> {
  return typeof value === "object" && value !== null;
}

async function verify(
  metadata: PipelineOperation,
  check: () => boolean,
  invariant: string,
  run: <T>(
    metadata: PipelineOperation,
    operation: () => Promise<T> | T,
  ) => Promise<T>,
): Promise<void> {
  await run(metadata, () => {
    if (!check()) throw new CallRecoveryInvariantError(invariant);
  });
}

/** Проверяет специальную причину немедленного полного повтора attempt. */
export function isNewEscalationAttemptError(error: unknown): boolean {
  return (
    error instanceof CallRecoveryAttemptError &&
    error.context.operation === "verify_no_new_escalation_signal"
  );
}
